import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { CardBody, CardHeader } from "reactstrap";
import Form from "./Form";
import NTable from "../../../components/NTable";
import Drawer from "../../../components/Drawer";
import Button from "../../../components/Button";
import { useDeleteModal } from "../../Settings/helpers/useDeleteModal";
import { useNewTable } from "../../../components/NTable/hook";
import { useDrawerProps } from "../../Settings/helpers/useDrawerProps";
import { useColumns } from "./columns";
import { ProfileContext } from "../context";
import { useRequestTimeOffApi } from "./api";
import { useCompanyDateFormat } from "../../../utils/hooks/company";
import { differenceFromNow, dateTimeFormat } from "../../../utils/helpers/date";
import { useVisible } from "../../../utils/hooks/useVisible";
import InfoModal from "./InfoModal";
import { useAccess, useActionWithAccess } from "../../../utils/hooks/access";

const useCustomDrawer = (getChildren) => {
	const [wrapperElement, setWrapperElement] = useState(null);

	useLayoutEffect(() => {
		const element = document.getElementById("timeoff-drawer");
		setWrapperElement(element);
	}, [setWrapperElement]);

	if (wrapperElement === null) return null;

	return createPortal(getChildren(), wrapperElement);
};

function TimeOff({ isOpenFromTimesheet }) {
	const { t } = useTranslation();
	const { user, isSelf } = useContext(ProfileContext);
	const dateFormat = useCompanyDateFormat();
	const { visible, open, close, selected: selectedPendingItems } = useVisible();
	const { hasAccess: canCreate } = useAccess(isSelf ? "profile.timeOff.canCreate" : "people.timeOff.canCreate");

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		create,
		update,
		goToView,
		goToCreate,
		goToEdit,
		goToClone,
		remove,
		goToTransfer,
		transfer,
	} = useRequestTimeOffApi();

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: () => t("do-you-want-to-delete-plan"),
		onConfirm: (data) => remove(data),
		permission: isSelf ? "profile.timeOff.canDelete" : "people.timeOff.canDelete",
	});

	const edit = useActionWithAccess(
		goToEdit,
		isSelf ? "profile.timeOff.canEdit" : "people.timeOff.canEdit",
	);

	const clone = useActionWithAccess(
		goToClone,
		isSelf ? "profile.timeOff.canCreate" : "people.timeOff.canCreate",
	);

	const _transfer = useActionWithAccess(
		goToTransfer,
		isSelf ? "profile.timeOff.canTransfer" : "people.timeOff.canTransfer"
	);

	const columns = useColumns({
		edit,
		remove: openDeleteModal,
		clone,
		isOpenFromTimesheet,
		transfer: _transfer,
		open
	});
	const table = useNewTable({
		data,
		totalItems,
		loading,
		columns,
		enableRowSelection: false,
	});

	const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
		mode,
		create: t("add-new-plan"),
		edit: t("edit-plan"),
		clone: t("clone-plan"),
		transfer: t("transfer-plan")
	});

	const drawer = useCustomDrawer(() => {
		return (
			<Drawer
				title={drawerTitle}
				open={drawerOpen}
				close={goToView}
				style={{
					maxWidth: "initial",
				}}
			>
				<Form
					mode={mode}
					error={error}
					loading={submitting}
					close={goToView}
					create={create}
					update={update}
					transfer={transfer}
					values={selected}
				/>
			</Drawer>
		);
	});

	useEffect(() => {
		if (!user) return;
		const controller = new AbortController();
		fetch(controller);
		return () => controller.abort();
	}, [fetch, user]);

	return (
		<>
			{!isOpenFromTimesheet && (
				<CardHeader className="border-0">
					<div className="d-flex justify-content-between">
						<div className="d-flex flex-column">
							<h3>
								{t("length-of-service")}:{" "}
								{user?.hiringDate &&
									dateTimeFormat(user?.hiringDate, dateFormat)}
							</h3>

							<p>
								{user?.hiringDate &&
									differenceFromNow(user?.hiringDate)}
							</p>
						</div>

						{canCreate && (
							<Button
								onClick={goToCreate}
								className="border-dark pr-3 btn-round btn-icon shadow-none btn btn-none btn-sm h-25"
							>
								{t("assign-plan")}
							</Button>
						)}
					</div>
				</CardHeader>
			)}

			<CardBody
				className="p-0"
				style={{
					height: "calc(100% - 20px)",
					overflowY: "auto",
				}}
			>
				<NTable table={table} />
			</CardBody>

			{drawer}
			<InfoModal
				values={selectedPendingItems}
				isOpen={visible}
				close={close}
			/>
			{deleteModal}
		</>
	);
}

export default TimeOff;
